import useCalendarStore from '@/store/calendarStore';
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import TableLoadingSpinner from '../ui/table/TableLoadingSpinner';
import DetailForm from './detailForm/DetailForm';
import { isMobile } from 'react-device-detect';
import {
  useQueryCalendar,
  useQueryYearlyCalendar,
} from '@/query/calendar/useQueryCalendar';
import {
  SELECT_TYPE,
  CALENDAR_ITEM_COLOR,
  DESC_KEYS,
} from '@/constants/calendar';

export const Detail = ({
  eventId,
  hideCloseButton,
}: {
  eventId?: string;
  hideCloseButton?: boolean;
}) => {
  return (
    <DetailLayout>
      <DetailContent eventId={eventId} hideCloseButton={hideCloseButton} />
    </DetailLayout>
  );
};
export default Detail;

const DetailContent = ({
  eventId,
  hideCloseButton,
}: {
  eventId?: string;
  hideCloseButton?: boolean;
}) => {
  const { setCheckedAllTrue, isLoading, getEventById, setEvents } =
    useCalendarStore();
  const [searchParams] = useSearchParams();
  const [isLoadingEvent, setIsLoadingEvent] = useState(false);

  const id = eventId || searchParams.get('id');

  const { event } = !isLoading && getEventById(id);

  const currentDate = new Date();
  const [hasSearchedYearly, setHasSearchedYearly] = useState(false);

  const { data: globalEvents, isLoading: globalLoading } = useQueryCalendar(
    SELECT_TYPE[0],
    '',
    currentDate,
    !event && !isLoading && !hasSearchedYearly
  );
  const { data: koreaEvents, isLoading: koreaLoading } = useQueryCalendar(
    SELECT_TYPE[1],
    '',
    currentDate,
    !event && !isLoading && !hasSearchedYearly
  );

  const { data: yearlyGlobalEvents, isLoading: yearlyGlobalLoading } =
    useQueryYearlyCalendar(
      SELECT_TYPE[0],
      '',
      !event && !isLoading && hasSearchedYearly
    );
  const { data: yearlyKoreaEvents, isLoading: yearlyKoreaLoading } =
    useQueryYearlyCalendar(
      SELECT_TYPE[1],
      '',
      !event && !isLoading && hasSearchedYearly
    );

  const initEvents = useCallback(
    (events: any[], type: string) => {
      const parsedEvents = events.map(event => parseEvents(event));
      const color = CALENDAR_ITEM_COLOR[type];
      const eventsWithColor = parsedEvents.map(event => ({
        ...event,
        backgroundColor: color,
        borderColor: color,
      }));

      const { events: existingEvents } = useCalendarStore.getState();
      const existingEventsForType = existingEvents[type] || [];

      const newEvents = eventsWithColor.filter(
        newEvent =>
          !existingEventsForType.some(existing => existing.id === newEvent.id)
      );

      const mergedEvents = existingEventsForType.concat(newEvents);
      setEvents(mergedEvents, type);
    },
    [setEvents]
  );

  useEffect(() => {
    setCheckedAllTrue();
  }, [setCheckedAllTrue]);

  useEffect(() => {
    if (!event && !isLoading && !isLoadingEvent) {
      setIsLoadingEvent(true);
    }
  }, [event, isLoading, isLoadingEvent, id]);

  useEffect(() => {
    if (
      isLoadingEvent &&
      !globalLoading &&
      !koreaLoading &&
      !hasSearchedYearly
    ) {
      let foundEvent = false;
      if (globalEvents && globalEvents.length > 0) {
        initEvents(globalEvents, 'GLOBAL');
        if (globalEvents.some(e => e.id === id)) foundEvent = true;
      }
      if (koreaEvents && koreaEvents.length > 0) {
        initEvents(koreaEvents, 'KOREA');
        if (koreaEvents.some(e => e.id === id)) foundEvent = true;
      }

      if (foundEvent) {
        setIsLoadingEvent(false);
      } else {
        setHasSearchedYearly(true);
      }
    }
  }, [
    isLoadingEvent,
    globalLoading,
    koreaLoading,
    globalEvents,
    koreaEvents,
    initEvents,
    id,
    hasSearchedYearly,
  ]);

  useEffect(() => {
    if (
      isLoadingEvent &&
      hasSearchedYearly &&
      !yearlyGlobalLoading &&
      !yearlyKoreaLoading
    ) {
      if (yearlyGlobalEvents && yearlyGlobalEvents.length > 0) {
        initEvents(yearlyGlobalEvents, 'GLOBAL');
      }
      if (yearlyKoreaEvents && yearlyKoreaEvents.length > 0) {
        initEvents(yearlyKoreaEvents, 'KOREA');
      }
      setIsLoadingEvent(false);
    }
  }, [
    isLoadingEvent,
    hasSearchedYearly,
    yearlyGlobalLoading,
    yearlyKoreaLoading,
    yearlyGlobalEvents,
    yearlyKoreaEvents,
    initEvents,
    id,
  ]);

  if (isLoading || isLoadingEvent)
    return <TableLoadingSpinner width={'3rem'} height={'3rem'} />;

  if (!event) return <div>No Event Found</div>;

  return <DetailForm event={event} hideCloseButton={hideCloseButton} />;
};

const DetailLayout = ({ children }) => {
  return <Layout $ismobile={isMobile}>{children}</Layout>;
};

export const Layout = styled.main<{ $ismobile: boolean }>`
  position: relative;
  width: ${({ $ismobile }) => ($ismobile ? '90%' : '80%')};
  height: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
`;

const parseEvents = (event: any) => {
  event = Object.entries(event).reduce((acc, [key, value]) => {
    return { ...acc, [key]: !!value ? value : '' };
  }, {});

  let parsedDescription;
  if (event.description) {
    parsedDescription = parseDescription(event.description);
  }

  return {
    ...event,
    description: { ...parsedDescription },
  };
};

function convertBrToNewlineAndStripHtmlTags(str: string) {
  let result = str.replace(/<br\s*\/?>/gi, '\n');

  result = result.replace(/<\/?[^>]+(>|$)/g, '');

  return result;
}

const parseDescription = (description: string) => {
  const formattedDescription = convertBrToNewlineAndStripHtmlTags(description);
  const lines: string[] = formattedDescription.split('\n');

  const descIndex = lines.findIndex(line => line.indexOf('Description') !== -1);

  if (descIndex === -1) {
    return {};
  }
  const informations = lines.slice(0, descIndex);
  const formattedInformations = informations
    .map(info => {
      const colonIndex = info.indexOf(':');
      if (colonIndex !== -1) {
        let key = info.slice(0, colonIndex).trim();
        let value: string | string[] = info.slice(colonIndex + 1).trim();

        if (key === 'Speakers') {
          value = customSplit(value);
        } else if (value.length === 1) {
          value = value[0];
        }

        return { key, value };
      }
      return null;
    })
    .filter(Boolean);

  const descFirstLine = lines[descIndex].split('Description:')[1];
  const descSecondLine = lines.slice(descIndex + 1).join('\n');

  const desc = (descFirstLine + '\n' + descSecondLine).trim();

  const resultList = [
    ...formattedInformations,
    { key: 'Description', value: desc },
  ];

  return resultList.reduce((acc, curr) => {
    const descKey = Object.entries(DESC_KEYS).filter(
      ([key, value]) => value === curr.key
    );
    if (descKey.length === 0) {
      return acc;
    }

    const key = descKey[0][0];

    acc[key] = curr.value;

    return acc;
  }, {});
};

function customSplit(str: string) {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let inParentheses = 0;

  const trimmedStr = str.replace(/\[|\]/g, '');

  for (let i = 0; i < trimmedStr.length; i++) {
    const char = trimmedStr[i];

    if (char === "'") {
      inQuotes = !inQuotes;
      current += char;
    } else if (char === '(') {
      inParentheses++;
      current += char;
    } else if (char === ')') {
      inParentheses--;
      current += char;
    } else if (char === ',' && !inQuotes && inParentheses === 0) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  if (current) {
    result.push(current.trim());
  }

  return result.map(item => item.replace(/^'|'$/g, '').trim());
}
